const getHeadingLevel = (block) => {
  const { level } = block.props || {};
  return `h${level}`;
};

/**
 * Builds an HTML string for the provided tag and inner HTML.
 * - Inlines text alignment when supplied.
 * - Applies heading-specific inline styles (font size/weight) for h1–h6 tags.
 */
const buildAlignedHtml = (tag, innerHtml, alignment) => {
  const headingTagRegex = /^h[1-6]$/i;
  const styles = [];
  if (alignment) {
    styles.push(`text-align: ${alignment}`);
  }
  if (tag && headingTagRegex.test(tag)) {
    const headingSizes = {
      h1: "24",
      h2: "22",
      h3: "20",
      h4: "18",
      h5: "18",
      h6: "16"
    };
    styles.push(
      `font-size: ${headingSizes[String(tag).toLowerCase()]}px`,
      "line-height: 1.5",
      "font-weight: 600"
    );
  }
  const styleAttr = styles.length ? ` style="${styles.join("; ")};"` : "";
  return tag ? `<${tag}${styleAttr}>${innerHtml}</${tag}>` : innerHtml;
};

const getRichTextComponentFromCoreBlock = (block) => {
  const { blockName, props } = block || {};
  const safeProps = props && typeof props === "object" ? props : {};
  const { content, align, textAlign, level } = safeProps;
  const alignment = align || textAlign;
  const safeHtml =
    content == null || Number.isNaN(content) ? "" : String(content);

  let htmlContent = safeHtml;

  if (blockName === "core/paragraph") {
    htmlContent = buildAlignedHtml("p", safeHtml, alignment);
  } else if (blockName === "core/heading") {
    const tag = getHeadingLevel({ props: { level } }) || "h1";
    htmlContent = buildAlignedHtml(tag, safeHtml, alignment);
  } else if (blockName === "core/html") {
    htmlContent = safeHtml;
  }

  return {
    blockName: "gwp-components/rich-text",
    props: {
      htmlContent,
      typeramp: "text-size-inherit"
    }
  };
};

const getListComponentFromList = (block) => {
  const { ordered } = block.props || {};
  return {
    blockName: "gwp-components/list",
    props: {
      "list-type": ordered ? "ol" : "ul",
      attributes: {
        style: {
          listStyleType: ordered ? "decimal" : "disc",
          marginLeft: "20px"
        }
      },
      fontSize: ""
    }
  };
};

const getListItemComponentFromListItem = (block) => {
  const { content } = block.props || {};
  const existingInnerBlocks = Array.isArray(block.innerBlocks)
    ? block.innerBlocks.filter(
        (b) => b && b.blockName !== "gwp-components/rich-text"
      )
    : [];
  block.innerBlocks = [
    {
      blockName: "gwp-components/rich-text",
      props: {
        htmlContent: `<p>${content || ""}</p>`,
        typeramp: "text-size-inherit"
      }
    },
    ...existingInnerBlocks
  ];

  return {
    blockName: "gwp-components/list-item",
    props: {}
  };
};

const getExpandableFromExpando = (block) => {
  const { title, level } = block.props || {};

  return {
    blockName: "gwp-components/expandable",
    props: {
      label: title || "",
      labelAs: `h${level || 1}`,
      chevronLocation: "right",
      enableLabelStyles : true
    }
  };
};
/**
 * Parse a YouTube URL and return a videoId and/or playlistId.
 */
const parseYouTubeUrl = (value) => {
  if (!value || typeof value !== "string") {
    return {};
  }
  const str = String(value).trim();
  const result = {};

  const vMatch = str.match(/[?&]v=([^&]+)/i);
  if (vMatch && vMatch[1]) {
    result.videoId = vMatch[1];
  }

  const listMatch = str.match(/[?&]list=([^&]+)/i);
  if (listMatch && listMatch[1]) {
    result.playlistId = listMatch[1];
  }

  if (result.videoId || result.playlistId) {
    return result;
  }

  // Handle direct video links (youtu.be, embed, shorts)
  const directMatch = str.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|shorts\/))([A-Za-z0-9_-]{6,})/i
  );
  if (directMatch && directMatch[1]) {
    return { videoId: directMatch[1] };
  }

  // Fallback: treat entire string as a video id
  return { videoId: str };
};

const getContainerComponentFromCoreGroup = (block) => {
  const { innerBlocks } = block.props || {};
  return {
    blockName: "gwp-components/container",
    props: { innerBlocks: innerBlocks || [] }
  };
};
/**
 * Helpers for columns mapping
 */
const normalizePercent = (value) => {
  if (typeof value === "number") return `${value}%`;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return /%$/.test(trimmed) ? trimmed : `${trimmed}%`;
  }
  return undefined;
};

const buildFlexWidthStyle = (widthVal) => {
  const width = normalizePercent(widthVal);
  return width ? { flex: `0 0 ${width}` } : { flex: "1 1 auto" };
};

/**
 * Map core/columns to a big container that holds one child container per core/column.
 */
const getContainerFromCoreColumns = (block) => {
  const columns = Array.isArray(block.innerBlocks) ? block.innerBlocks : [];
  const columnContainers = columns.map((col) => ({
    blockName: "gwp-components/container",
    props: { attributes: { style: buildFlexWidthStyle(col?.props?.width) } },
    innerBlocks: Array.isArray(col.innerBlocks) ? col.innerBlocks : []
  }));

  return {
    blockName: "gwp-components/container",
    props: {
      cssClasses:
        "flex flex-col md:flex-row w-full min-h-screen items-center md:items-stretch -mx-2"
    },
    innerBlocks: columnContainers
  };
};

/**
 * Map a single core/column to a container, passing its inner content.
 */
const getContainerFromCoreColumn = (block) => {
  const innerBlocks = Array.isArray(block.innerBlocks) ? block.innerBlocks : [];
  return {
    blockName: "gwp-components/container",
    props: {
      cssClasses:
        "w-full md:w-auto px-2 min-w-0 box-border mx-auto md:mx-0 text-center md:text-left",
      attributes: { style: buildFlexWidthStyle(block?.props?.width) }
    },
    innerBlocks
  };
};
/**
 * Builds a gwp-components/youtube block from a core/embed block.
 */
const getYouTubeFromCoreEmbed = (block) => {
  const { url } = block.props || {};
  const { videoId, playlistId } = parseYouTubeUrl(
    block.blockName === "helix/tutorial" ? block?.props?.videoLink : url
  );

  const youtubeProps = {
    autoPlay: false,
    controls: true,
    loop: true,
    playsinline: true,
    attributes: {
      style: {
        height: "100%",
        width: "100%"
      }
    }
  };

  if (videoId) {
    youtubeProps.videoId = videoId;
  } else if (playlistId) {
    youtubeProps.playlistId = playlistId;
  } else if (url) {
    youtubeProps.videoId = url;
  }

  // Mutate the original block to add innerBlocks with the YouTube component
  block.innerBlocks = [
    {
      blockName: "gwp-components/youtube",
      props: youtubeProps
    }
  ];

  // Wrap YouTube component in a container div with aspect ratio styling
  return {
    blockName: "gwp-components/container",
    props: {
      attributes: {
        style: {
          aspectRatio: "16/9",
          boxSizing: "border-box",
          margin: "0px 0px 10px"
        }
      }
    }
  };
};

const getTableFromCoreTable = (block) => {
  const { head, body } = block.props || {};
  const headerData = Array.isArray(head)
    ? (head[0]?.cells || []).map((cell) => cell.content)
    : [];
  const rowsData = Array.isArray(body)
    ? body.map((row) => (row.cells || []).map((cell) => cell.content))
    : [];
  const isHeaderAvailable = Array.isArray(head) && head.length > 0;
  return {
    blockName: "gwp-components/gwp-table",
    props: {
      rowCount: rowsData.length,
      columnCount:
        (headerData && headerData.length) ||
        (rowsData && rowsData[0] && rowsData[0].length) ||
        0,
      showHeader: isHeaderAvailable,
      headerData: isHeaderAvailable ? headerData : undefined,
      rowsData: rowsData,
      border: "round",
      divider: "all",
      responsive: "stack"
    }
  };
};

// Mirrors the getAssetType + fileTypes logic from digitalasset.ts,
// wired to gwp-components/image and gwp-components/video instead of raw styled elements.
const getAssetExtension = (url) => {
  if (!url || typeof url !== "string") return "";
  return url.split(".").pop().split("?")[0].toLowerCase();
};

const getDigitalAssetFromHelixDAS = (block) => {
  const das = block.props?.["digital-asset-service"];
  // CMS sends callbackUrl as a plain string; digitalasset.ts used the nested .value.callbackUrl.value shape
  const assetUrl = das?.callbackUrl || das?.value?.callbackUrl?.value;
  if (!assetUrl || typeof assetUrl !== "string")
    return { blockName: null, props: {} };

  const ext = getAssetExtension(assetUrl);
  const testId = `${ext}_${assetUrl}`;

  if (ext === "png" || ext === "jpg" || ext === "jpeg" || ext === "svg" || ext === "gif") {
    return {
      blockName: "gwp-components/image",
      props: {
        imageSet: JSON.stringify({ default: assetUrl }),
        altTag: "image",
        attributes: { "data-testid": testId }
      }
    };
  }

  if (ext === "mp4") {
    return {
      blockName: "gwp-components/video",
      props: {
        videoSources: [{ src: assetUrl, type: "video/mp4" }],
        controls: true,
        ariaLabel: "video",
        attributes: { "data-testid": testId }
      }
    };
  }

  // Unsupported type — mirrors HDS empty fragment
  return { blockName: null, props: {} };
};

const getHelixBannerFromHelixBanner = (block) => {
  const { bannertype, description, className, cssClasses } = block.props || {};
  const props = {};
  if (bannertype !== undefined) props.bannerType = bannertype;
  if (description !== undefined) {
    block.innerBlocks = [
      {
        blockName: "gwp-components/rich-text",
        props: { htmlContent: description, typeramp: "text-size-inherit" }
      }
    ];
  }
  const resolvedCssClasses = cssClasses ?? className;
  if (resolvedCssClasses !== undefined) props.cssClasses = resolvedCssClasses;
  return { blockName: "gwp-components/helix-banner", props };
};

/**
 * Checks if the block is a core block
 *
 * @param {string} blockName - The name of the block
 * @returns {boolean} True if the block is a core block, false otherwise
 */
const isCoreBlock = (blockName) => {
  if (!blockName || typeof blockName !== "string") {
    return false;
  }
  return (
    blockName.startsWith("core/") ||
    blockName.startsWith("core-embed/") ||
    blockName.startsWith("helix/")
  );
};

/**
 * Transforms a core block to a GWP component
 * @param {object} block - The block to transform
 * @returns {object} The transformed block
 */
const transformCoreBlockToGwpComponent = (block) => {
  const { blockName } = block;

  switch (blockName) {
    case "core/paragraph":
      return getRichTextComponentFromCoreBlock(block);
    case "core/heading":
      return getRichTextComponentFromCoreBlock(block);
    case "core/html":
      return getRichTextComponentFromCoreBlock(block);
    case "core/list":
      return getListComponentFromList(block);
    case "core/list-item":
      return getListItemComponentFromListItem(block);
    case "helix/expando":
      return getExpandableFromExpando(block);
    case "core/embed":
    case "helix/tutorial":
    case "core-embed/youtube":
      return getYouTubeFromCoreEmbed(block);
    case "core/group":
      return getContainerComponentFromCoreGroup(block);
    case "core/columns":
      return getContainerFromCoreColumns(block);
    case "core/column":
      return getContainerFromCoreColumn(block);
    case "core/table":
      return getTableFromCoreTable(block);
    case "helix/banner":
      return getHelixBannerFromHelixBanner(block);
    case "helix/digital-asset-service-block":
      return getDigitalAssetFromHelixDAS(block);
    default:
      return block;
  }
};

/**
 * Transforms a core block to a GWP component
 * @param {object} block - The block to transform
 * @returns {object} The transformed block
 */
const transformCoreBlock = (block) => {
  if (!block || typeof block !== "object") {
    return block;
  }
  const { blockName } = block;
  return isCoreBlock(blockName)
    ? transformCoreBlockToGwpComponent(block)
    : block;
};

export {
  getRichTextComponentFromCoreBlock,
  getListComponentFromList,
  getListItemComponentFromListItem,
  getExpandableFromExpando,
  getHelixBannerFromHelixBanner,
  getDigitalAssetFromHelixDAS,
  isCoreBlock,
  transformCoreBlockToGwpComponent
};

export default transformCoreBlock;
